import { useMutation, useQuery } from '@tanstack/react-query'

import { apiClient } from '../../apiClient'

export const ORG_LIST_KEY = 'org-list'
export const ORG_BY_ID_KEY = 'org-by-id'

type OrganizationStatus = 'active' | 'inactive'

type OrganizationListParamsRequest = {
  status?: OrganizationStatus
  cursor: number
  limit: number
  orderBy?: string
  sortDescending?: boolean
}

type OrganizationData = {
  id: string
  name: string
  logoUrl?: string
  status: OrganizationStatus
  createdAt: string
  usersCount: number
}

type OrganizationResponse = {
  total: number
  organizations: OrganizationData[]
}

type OrganizationCreateRequestPayload = {
  name: string
  logoMediaId?: string
}

type OrganizationUpdateRequestPayload = {
  id: string
  logoMediaId?: string | null
  name?: string
  status?: OrganizationStatus
}

export function useGetList(params: OrganizationListParamsRequest) {
  return useQuery({
    queryKey: [ORG_LIST_KEY, params],
    queryFn: async () => {
      const { data } = await apiClient.get<OrganizationResponse>('/int/v3/organizations', { params })
      return data
    },
    refetchOnWindowFocus: false,
  })
}

export function useGetById(orgId?: string) {
  return useQuery({
    queryKey: [ORG_BY_ID_KEY, orgId],
    queryFn: async () => {
      const { data } = await apiClient.get<OrganizationData>(`/int/v3/organizations/${orgId}`)
      return data
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(orgId),
  })
}

export function useCreate() {
  return useMutation({
    mutationFn: async (payload: OrganizationCreateRequestPayload) => {
      return await apiClient.post('/int/v3/organizations/', payload)
    },
  })
}

export function useUpdate() {
  return useMutation({
    mutationFn: async ({ id, ...payload }: OrganizationUpdateRequestPayload) => {
      return await apiClient.put(`/int/v3/organizations/${id}`, payload)
    },
  })
}
